import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export type AppNotification = {
  id: string;
  title: string;
  body: string;
  /** Na šta notifikacija vodi; danas samo `bug_report`. Prazno = bez linka. */
  linkKind: string;
  linkId: string;
  readAt: number | null;
  createdAt: number;
};

export type NotificationList = { items: AppNotification[]; unread: number };

export const NOTIFICATIONS_KEY = ["notifications"] as const;

export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => fetchJson<NotificationList>("/api/notifications"),
    // Bez websocketa: minuta je dovoljno svježe za odgovore na prijave.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Optimistično označi pročitanim u kešu — zvonce se odmah ažurira, a server
 * potvrđuje ponovnim dohvatom nakon mutacije.
 */
function markLocally(qc: QueryClient, ids: string[] | "all") {
  const now = Math.floor(Date.now() / 1000);
  qc.setQueryData<NotificationList>(NOTIFICATIONS_KEY, (prev) => {
    if (!prev) return prev;
    let cleared = 0;
    const items = prev.items.map((n) => {
      if (n.readAt !== null || (ids !== "all" && !ids.includes(n.id))) return n;
      cleared += 1;
      return { ...n, readAt: now };
    });
    return { items, unread: ids === "all" ? 0 : Math.max(0, prev.unread - cleared) };
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson<void>(`/api/notifications/${id}/read`, { method: "POST" }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      markLocally(qc, [id]);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fetchJson<void>("/api/notifications/read-all", { method: "POST" }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      markLocally(qc, "all");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}
